import React, { useState } from 'react';
import { Tooltip, Dropdown, Menu } from 'antd';
import moment from 'moment';
import {
  Calendar,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Users,
  Percent,
  ArrowRight,
} from 'lucide-react';
import { MoreOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import {
  formatCurrency2SigFig,
  formatWholeNumberNaN,
  computeROAS,
} from '../../../../../core/utils/campaigns';
import {
  roasFormat,
  defaultRoasFormat,
} from '../../../../../core/utils/constants/constants';
import KpiBar, {
  KpiCard,
} from '../../../../shared/designSystem/components/KpiBar';
import { formatFlightDate } from '../../../../shared/designSystem/utils/dateFormat';

// =============================================================================
// TransactionsKpiBar — v2 KPI bar for the Transactions tab's unified
// Performance card. Uses the shared `KpiBar` + `KpiCard` primitives;
// FlightDatesCard stays local (flight-specific progress-bar body).
//
// Props mirror DetailBar4ColV2 for drop-in replacement:
//   data            { startDate, endDate, transactions, transactionRevenue,
//                     customers, impressions, spend }
//   priceVisible    boolean — gates Spend + ROAS columns
//   dataConfig      org dataConfig (provides org roasFormat)
//   currencyFormat  org currency symbol/format
// =============================================================================

const FlightDatesCard = ({ startDate, endDate, progressPercent }) => (
  <div className="campaign-overview__kpi">
    <div className="campaign-overview__kpi-head">
      <span className="campaign-overview__kpi-icon campaign-overview__kpi-icon--neutral">
        <Calendar size={16} />
      </span>
      <Tooltip
        overlayClassName="mjl-ds"
        title="Flight Dates refers to the Start & End Dates of the individual campaign"
      >
        <span className="campaign-overview__kpi-eyebrow">Flight Dates</span>
      </Tooltip>
    </div>
    <div className="campaign-overview__flight">
      <div className="campaign-overview__flight-dates">
        {/* UTC-anchored via formatFlightDate — same handling as the Display
            tab's flight card. Flight dates are stored UTC-midnight, so
            rendering them in the browser's zone shifts the day (a UTC
            end-of-day Jul 31 reads as "Aug 01" east of UTC). */}
        <span>{formatFlightDate(startDate, '—')}</span>
        <ArrowRight size={14} className="campaign-overview__flight-arrow" />
        <span>{formatFlightDate(endDate, '—')}</span>
      </div>
      <div className="campaign-overview__flight-progress">
        <div
          className="campaign-overview__flight-progress-bar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="campaign-overview__flight-meta">
        {progressPercent}% elapsed
      </div>
    </div>
  </div>
);

const TransactionsKpiBar = ({ data, priceVisible, dataConfig, currencyFormat }) => {
  const [roasFormatSelected, setRoasFormatSelected] = useState(
    (dataConfig && dataConfig.roasFormat) || defaultRoasFormat.key
  );

  // Same flight-date progress logic as v1's DetailBar4ColV2.
  const getProgress = () => {
    const a = moment(data && data.startDate);
    const b = moment(data && data.endDate);
    const totaldays = b.diff(a, 'days');
    const progressDays = moment().diff(moment(data && data.startDate), 'days');
    if (progressDays <= 0) return 100;
    if (progressDays >= totaldays) return 100;
    const daysin = totaldays - (totaldays - progressDays);
    return Math.floor((daysin / totaldays) * 100);
  };

  const currentposition = getProgress();

  return (
    <KpiBar>
      <FlightDatesCard
        startDate={data && data.startDate}
        endDate={data && data.endDate}
        progressPercent={currentposition}
      />

      <KpiCard
        icon={<ShoppingCart size={16} />}
        iconTint="primary"
        label="Transactions"
        value={data ? formatWholeNumberNaN(data.transactions) : '0'}
      />

      <KpiCard
        icon={<TrendingUp size={16} />}
        iconTint="emerald"
        label="Influenced Sales"
        labelTooltip="Multi-Touch Attribution (MTA). Using device-level data, this MTA model shows impressions have been served to device identifiers that have also made transactions. This model does not take into account other factors that may have influenced the purchase such as search, newsletters or other marketing techniques. MTA should also not be confused with other models. ie: Last-Interaction or Position-based"
        value={formatCurrency2SigFig(data ? data.transactionRevenue : 0, currencyFormat)}
      />

      <KpiCard
        icon={<Users size={16} />}
        iconTint="indigo"
        label="Customers"
        value={data ? formatWholeNumberNaN(data.customers) : '0'}
      />

      {priceVisible && (
        <KpiCard
          icon={<DollarSign size={16} />}
          iconTint="amber"
          label="Spend"
          value={formatCurrency2SigFig(data ? data.spend : 0, currencyFormat)}
        />
      )}

      {priceVisible && (
        <KpiCard
          icon={<Percent size={16} />}
          iconTint="purple"
          label="ROAS"
          value={computeROAS(data.transactionRevenue, data.spend, roasFormatSelected)}
          trailing={
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Dropdown
                placement="bottomRight"
                overlayClassName="mjl-ds"
                overlay={
                  <Menu>
                    {roasFormat.map((d) => (
                      <Menu.Item
                        key={d.key}
                        onClick={() => setRoasFormatSelected(d.key)}
                      >
                        {d.name}
                      </Menu.Item>
                    ))}
                  </Menu>
                }
              >
                <button
                  type="button"
                  className="campaign-overview__kpi-more"
                  aria-label="ROAS format options"
                >
                  <MoreOutlined />
                </button>
              </Dropdown>
              <Tooltip
                overlayClassName="mjl-ds"
                title="ROAS Format can be changed by pressing the more icon besides this tooltip. Select the desired format of the ROAS on the dropdown. If the account is admin it can be changed in the org config"
                arrowPointAtCenter={true}
                placement="bottomRight"
              >
                <QuestionCircleOutlined className="campaign-overview__kpi-help" />
              </Tooltip>
            </div>
          }
        />
      )}
    </KpiBar>
  );
};

export default TransactionsKpiBar;
